import type { ReactNode } from 'react';
import { BrandMark } from './BrandMark';
import { cn } from './format';

export type IconRailItem = {
  id: string;
  label: string;
  icon: ReactNode;
  href?: string;
  onClick?: () => void;
  badge?: boolean;
};

export type IconRailProps = {
  items: IconRailItem[];
  bottomItems?: IconRailItem[];
  activeId: string;
  logo?: ReactNode;
  onNavigate: (id: string) => void;
};

export function IconRail({ items, bottomItems = [], activeId, logo, onNavigate }: IconRailProps) {
  const renderItem = (item: IconRailItem) => {
    const active = item.id === activeId;
    return (
      <button
        key={item.id}
        type="button"
        title={item.label}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          item.onClick?.();
          onNavigate(item.id);
        }}
        className={cn(
          'relative flex h-11 w-11 items-center justify-center rounded-xl transition-colors',
          active
            ? 'bg-[color-mix(in_srgb,var(--accent)_22%,transparent)] text-[var(--accent)] ring-1 ring-[color-mix(in_srgb,var(--accent)_35%,transparent)]'
            : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]',
        )}
      >
        {item.icon}
        {item.badge && (
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-[var(--beat)]" />
        )}
      </button>
    );
  };

  return (
    <nav
      aria-label="Primary"
      className="flex h-screen w-[var(--rail-width)] shrink-0 flex-col items-center border-r border-[var(--border-subtle)] bg-[var(--bg)] py-4"
      style={{
        backgroundImage:
          'linear-gradient(180deg, var(--rail-tint), transparent 42%), linear-gradient(var(--bg), var(--bg))',
      }}
    >
      <div className="mb-6 flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl" title="Nxivo">
        {logo ?? <BrandMark size={40} product="nxivo" />}
      </div>
      <div className="flex flex-1 flex-col items-center gap-1">{items.map(renderItem)}</div>
      <div className="flex flex-col items-center gap-1">{bottomItems.map(renderItem)}</div>
    </nav>
  );
}
